/**
 * P2b — the command palette's STATIC route index (maquette 006 « Navigate »).
 *
 * WHY a table and not a scan of <Route>: a route is not a page the member may
 * open. Every entry below mirrors ONE real `<Route>` in `src/App.tsx` and
 * carries the SAME visibility rule the SIDEBAR applies to that link — the
 * palette must never offer a page the server (or the nav) refuses:
 *
 *   - `always`        → sidebar link rendered unconditionally (Tour section)
 *   - `bandOnly`      → sidebar link inside the `activeProfileId &&` block
 *   - `page(id)`      → sidebar link behind `vis[id]` = `pageVisibility()` from
 *                       `src/lib/pageAccess.ts` (the « source unique »: it
 *                       already folds in the explicit READ DENY `isPageDenied`,
 *                       the rider/layout/finance capabilities and the roles)
 *   - `isAdmin`       → the sidebar's `(isSuperAdmin || isShowcase)` admin gate
 *                       (`pageVisibility()` deliberately returns `false` for
 *                       `users`/`apiKeys`, so the admin gate is read here)
 *
 * A band-scoped entry (`/band/:bandSlug/...`) is hidden whenever no band is
 * active — never render `/band//settings`.
 *
 * Digit shortcuts (⌘0-⌘9, `Ctrl+0-9` elsewhere) fire ONLY while the palette is
 * open: this module installs no global shortcut and captures nothing.
 */
import type { PageId } from './pageAccess';

/** Everything a command needs to know about the current session. */
export interface PaletteNavContext {
  /** `/band/<slug>` of the active band — '' when none. */
  base: string;
  /** A band is active (`activeProfileId` resolved to a slug). */
  band: boolean;
  /** The map AppLayout computed with `pageVisibility()` — same object as the sidebar. */
  vis: Record<string, boolean>;
  /** Platform admin or the shared showcase account (the sidebar's admin gate). */
  isAdmin: boolean;
}

export interface PaletteCommand {
  /** Stable technical id — QA hooks target `data-palette-id`, never a label. */
  id: string;
  /** i18n key (already namespaced; the palette never hardcodes a label). */
  labelKey: string;
  /** Decorative glyph rendered in the icon column. */
  icon: string;
  /** Extra filter words (the mockup's `data-t`), English/technical on purpose. */
  keywords: string;
  /** In-palette digit shortcut, `0`-`9`. */
  digit?: string;
  /** Real route — built from the context, never guessed. */
  route: (ctx: PaletteNavContext) => string;
  /** Same visibility rule the sidebar applies to the matching NavLink. */
  visible: (ctx: PaletteNavContext) => boolean;
}

/* ---- The four gates, named after where the sidebar puts the link. -------- */
const always = () => true;
/** Inside the `activeProfileId &&` block only. */
const bandOnly = (ctx: PaletteNavContext) => ctx.band;
/** Inside the band block AND behind `vis[id]` (roles / deny / capabilities). */
const page = (id: PageId) => (ctx: PaletteNavContext) => bandOnly(ctx) && !!ctx.vis[id];
/** The sidebar's `(isSuperAdmin || isShowcase)` admin section. */
const adminOnly = (ctx: PaletteNavContext) => ctx.isAdmin;

/**
 * THE index. Every `route` below matches a `<Route path=…>` in App.tsx —
 * verify with: grep -n '<Route path=' src/App.tsx
 */
export const NAV_COMMANDS: readonly PaletteCommand[] = [
  /* ---------------- Global (no band required) — the Tour section --------- */
  {
    id: 'home', labelKey: 'tabs.home', icon: '⌂', digit: '0',
    keywords: 'landing start band grid',
    route: () => '/home', visible: always,
  },
  {
    id: 'shows', labelKey: 'sidebar.shows', icon: '▤', digit: '1',
    keywords: 'shows list dates concerts',
    route: () => '/tour/shows', visible: always,
  },
  {
    id: 'calendar', labelKey: 'shows.calendar', icon: '▦', digit: '2',
    keywords: 'calendar month grid dates',
    route: () => '/tour/calendar', visible: always,
  },
  {
    id: 'itinerary', labelKey: 'sidebar.itinerary', icon: '⇢', digit: '3',
    keywords: 'itinerary travel days routing',
    route: () => '/tour/itinerary', visible: always,
  },
  {
    id: 'advancing', labelKey: 'sidebar.advancing', icon: '✓',
    keywords: 'advancing venue promoter advance',
    route: () => '/tour/advancing', visible: always,
  },
  {
    id: 'tours', labelKey: 'sidebar.tours', icon: '≡',
    keywords: 'tours list',
    route: () => '/tour/tours', visible: always,
  },
  {
    id: 'venues', labelKey: 'sidebar.venues', icon: '⚑', digit: '5',
    keywords: 'venues places halls rooms',
    route: () => '/tour/venues', visible: always,
  },
  {
    id: 'contacts', labelKey: 'sidebar.contacts', icon: '⊞', digit: '6',
    keywords: 'contacts address book promoters',
    route: () => '/tour/contacts', visible: always,
  },
  {
    id: 'team', labelKey: 'sidebar.team', icon: '◉', digit: '4',
    keywords: 'team people crew musicians members',
    route: () => '/tour/team', visible: bandOnly,
  },
  {
    id: 'aiAssistant', labelKey: 'sidebar.aiAssistant', icon: '✦',
    keywords: 'ai assistant chat copilot',
    route: () => '/ai-assistant', visible: always,
  },
  {
    id: 'bands', labelKey: 'bandList.title', icon: '◫',
    keywords: 'all bands groups list',
    route: () => '/bands', visible: always,
  },
  {
    id: 'account', labelKey: 'app.account', icon: '⚙', digit: '9',
    keywords: 'account profile password 2fa sessions',
    route: () => '/account', visible: always,
  },
  {
    id: 'pricing', labelKey: 'billing.title', icon: '★',
    keywords: 'pricing plans subscription billing',
    route: () => '/pricing', visible: always,
  },
  {
    id: 'help', labelKey: 'sidebar.help', icon: '✎',
    keywords: 'help wiki documentation tour',
    route: () => '/help', visible: always,
  },
  {
    // Tour Arcade (2026-11-04) — same global gate as the sidebar link; the
    // mobile tab bar is the other way in.
    id: 'games', labelKey: 'games.title', icon: '🕹',
    keywords: 'games arcade snake tetris pong fun offline airport wait',
    route: () => '/games', visible: always,
  },
  {
    id: 'preload', labelKey: 'preload.title', icon: '↓',
    keywords: 'offline preload pwa install cache',
    route: () => '/preload', visible: always,
  },

  /* ---------------- Band-scoped (/band/:bandSlug/…) ---------------------- */
  {
    // P2c — Atlas / Route map. Same gate as the sidebar link (`activeProfileId`),
    // so the palette never offers `/band//atlas` when no band is active.
    id: 'atlas', labelKey: 'sidebar.atlas', icon: '⌖',
    keywords: 'atlas route map pins stops geography tour',
    route: (ctx) => `${ctx.base}/atlas`, visible: bandOnly,
  },
  {
    // P2d — Day of show. Same gate as the sidebar link (`activeProfileId`),
    // so the palette never offers `/band//day-of` when no band is active.
    id: 'day-of', labelKey: 'sidebar.dayOf', icon: '◉',
    keywords: 'day of show today run of show agenda now doors countdown',
    route: (ctx) => `${ctx.base}/day-of`, visible: bandOnly,
  },
  {
    id: 'bandSettings', labelKey: 'sidebar.bandSettings', icon: '⚒',
    keywords: 'band settings members colors logo',
    route: (ctx) => `${ctx.base}/settings`, visible: page('bandSettings'),
  },
  {
    id: 'availability', labelKey: 'sidebar.availability', icon: '◷',
    keywords: 'availability dates free busy',
    route: (ctx) => `${ctx.base}/availability`, visible: bandOnly,
  },
  {
    id: 'musicians', labelKey: 'sidebar.musicians', icon: '♪',
    keywords: 'musicians players instruments roster',
    route: (ctx) => `${ctx.base}/musicians`, visible: page('musicians'),
  },
  {
    id: 'crew', labelKey: 'sidebar.crew', icon: '☗',
    keywords: 'crew technicians techs',
    route: (ctx) => `${ctx.base}/crew`, visible: page('crew'),
  },
  {
    id: 'setlists', labelKey: 'sidebar.setlists', icon: '♫', digit: '7',
    keywords: 'setlists songs music',
    route: (ctx) => `${ctx.base}/setlists`, visible: page('setlists'),
  },
  {
    id: 'songbook', labelKey: 'sidebar.songbook', icon: '❧',
    keywords: 'songbook songs repertoire',
    route: (ctx) => `${ctx.base}/songbook`, visible: page('songbook'),
  },
  {
    id: 'checklists', labelKey: 'sidebar.checklists', icon: '☑',
    keywords: 'checklists tasks todo',
    route: (ctx) => `${ctx.base}/checklists`, visible: page('checklists'),
  },
  {
    id: 'payouts', labelKey: 'sidebar.payouts', icon: '◆',
    keywords: 'payouts fees money salary finance',
    route: (ctx) => `${ctx.base}/payouts`, visible: page('payouts'),
  },
  /* Rider content (config + stage plot + preview): one page = one `vis[id]`,
     so a member refused `content.rider` never gets offered a page that 403s. */
  {
    id: 'configGeneral', labelKey: 'sidebar.configGeneral', icon: '⚙',
    keywords: 'config general setup schedule',
    route: (ctx) => `${ctx.base}/config/general`, visible: page('configGeneral'),
  },
  {
    id: 'configHospitality', labelKey: 'sidebar.configHospitality', icon: '⚙',
    keywords: 'config hospitality catering food dressing rooms',
    route: (ctx) => `${ctx.base}/config/hospitality`, visible: page('configHospitality'),
  },
  {
    id: 'configSound', labelKey: 'sidebar.configSound', icon: '⚙',
    keywords: 'config sound pa console microphones',
    route: (ctx) => `${ctx.base}/config/sound`, visible: page('configSound'),
  },
  {
    id: 'configIem', labelKey: 'sidebar.configIem', icon: '⚙',
    keywords: 'config iem in ear monitors mixes',
    route: (ctx) => `${ctx.base}/config/iem`, visible: page('configIem'),
  },
  {
    id: 'configAudioSpecs', labelKey: 'sidebar.configAudioSpecs', icon: '⚙',
    keywords: 'config audio specs technical',
    route: (ctx) => `${ctx.base}/config/audio-specs`, visible: page('configAudioSpecs'),
  },
  {
    id: 'configBackline', labelKey: 'sidebar.configBackline', icon: '⚙',
    keywords: 'config backline amps drums gear',
    route: (ctx) => `${ctx.base}/config/backline`, visible: page('configBackline'),
  },
  {
    id: 'configStageDesign', labelKey: 'sidebar.configStageDesign', icon: '⚙',
    keywords: 'config stage design backdrop risers',
    route: (ctx) => `${ctx.base}/config/stage-design`, visible: page('configStageDesign'),
  },
  {
    id: 'configLights', labelKey: 'sidebar.configLights', icon: '⚙',
    keywords: 'config lights fixtures lighting',
    route: (ctx) => `${ctx.base}/config/lights`, visible: page('configLights'),
  },
  {
    id: 'stagePlot', labelKey: 'sidebar.stagePlot', icon: '▩',
    keywords: 'stage plot editor plan',
    route: (ctx) => `${ctx.base}/stageplot-editor`, visible: page('stagePlot'),
  },
  {
    id: 'riderPreview', labelKey: 'sidebar.riderPreview', icon: '☰', digit: '8',
    keywords: 'rider tech pdf preview hospitality',
    route: (ctx) => `${ctx.base}/rider`, visible: page('riderPreview'),
  },
  /* Layout editors — one link per capability (the page guard would refuse the rest). */
  {
    id: 'riderLayout', labelKey: 'sidebar.riderLayout', icon: '⌗',
    keywords: 'layout editor rider pdf',
    route: (ctx) => `${ctx.base}/rider-layout`, visible: page('riderLayout'),
  },
  {
    id: 'daysheetLayout', labelKey: 'sidebar.daysheetLayout', icon: '⌗',
    keywords: 'layout editor daysheet pdf',
    route: (ctx) => `${ctx.base}/daysheet-layout`, visible: page('daysheetLayout'),
  },
  {
    id: 'tourbookLayout', labelKey: 'sidebar.tourBookLayout', icon: '⌗',
    keywords: 'layout editor tourbook pdf',
    route: () => '/tour/tourbook-layout', visible: page('tourBookLayout'),
  },

  /* ---------------- Platform admin (superadmin / showcase) --------------- */
  {
    id: 'users', labelKey: 'sidebar.users', icon: '⊕',
    keywords: 'admin users accounts access',
    route: () => '/users', visible: adminOnly,
  },
  {
    id: 'apiKeys', labelKey: 'sidebar.apiKeys', icon: '⚿',
    keywords: 'admin api keys tokens',
    route: () => '/api-keys', visible: adminOnly,
  },
  {
    id: 'managerOnboarding', labelKey: 'sidebar.connectAiAgent', icon: '⇗',
    keywords: 'manager onboarding agent invite',
    route: () => '/manager-onboarding', visible: adminOnly,
  },
];

/** `⌘` on Apple platforms, `Ctrl+` elsewhere — used in hints and the footer. */
export function modLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl+';
  const ua = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
  return /Mac|iPhone|iPad|iPod/.test(ua) ? '⌘' : 'Ctrl+';
}

/**
 * QA-only escape hatch (P2b negative control): with
 * `localStorage['bandz.palette.inertFilter'] === '1'` the instant filter stops
 * narrowing, so the harness can PROVE it detects a broken filter (state 2),
 * then clear the key and go green again (state 3). Unset = production.
 */
export function isFilterInert(): boolean {
  try {
    return localStorage.getItem('bandz.palette.inertFilter') === '1';
  } catch {
    return false;
  }
}
